// src/components/dictionary/DictionaryItem.jsx

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';

// ── Konfigurasi Warna per Level ───────────────────────────
const LEVEL_CONFIG = {
  'Dasar':    { badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',  dot: 'bg-green-500'  },
  'Menengah': { badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400',          dot: 'bg-sky-500'    },
  'Lanjutan': { badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', dot: 'bg-purple-500' },
};

// ── Konfigurasi Warna per Kategori (untuk teks Arab) ─────
const CATEGORY_COLOR = {
  'Kata Kerja':    'text-sky-700 dark:text-sky-300',
  "Marfu'at":      'text-green-700 dark:text-green-300',
  'Manshubat':     'text-orange-700 dark:text-orange-300',
  'Majrurat':      'text-purple-700 dark:text-purple-300',
  'Huruf':         'text-slate-700 dark:text-slate-300',
  "Tawabi'":       'text-indigo-700 dark:text-indigo-300',
  'Konsep Dasar':  'text-teal-700 dark:text-teal-300',
};

export default function DictionaryItem({ item, index }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied]         = useState(false);

  const handleCopy = () => {
    const text = [
      item.title,
      item.arabic,
      item.latin,
      item.description,
      item.example && `Contoh: ${item.example}`,
      item.translation && `Terjemah: ${item.translation}`,
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const levelCfg    = LEVEL_CONFIG[item.level]        || LEVEL_CONFIG['Dasar'];
  const arabicColor = CATEGORY_COLOR[item.category]   || 'text-slate-700';

  return (
    <div
      className="
        bg-white dark:bg-slate-800
        rounded-3xl
        shadow-soft
        border border-stone-100 dark:border-slate-700
        overflow-hidden
        animate-fade-in
        transition-shadow hover:shadow-soft-lg
      "
      style={{ animationDelay: `${index * 0.04}s` }}
    >
      {/* ── Header (Selalu Terlihat) ──────────────────── */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">

          {/* Konten Kiri */}
          <div className="flex-1 min-w-0">
            {/* Baris Atas: Level + Kategori */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {/* Dot Level */}
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${levelCfg.dot}`} />
                <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${levelCfg.badge}`}>
                  {item.level}
                </span>
              </div>

              {/* Kategori */}
              <span className="text-[10px] font-bold text-stone-500 dark:text-slate-400 bg-stone-100 dark:bg-slate-700 px-2.5 py-0.5 rounded-full">
                {item.category}
              </span>
            </div>

            {/* Judul Istilah */}
            <h3 className="text-sm font-extrabold text-slate-800 dark:text-slate-100 leading-tight mb-1">
              {item.title}
            </h3>

            {/* Teks Arab */}
            <p
              dir="rtl"
              className={`text-2xl font-bold mb-1 leading-relaxed ${arabicColor}`}
              style={{ fontFamily: 'Amiri, Noto Naskh Arabic, serif' }}
            >
              {item.arabic}
            </p>

            {/* Latin / Transliterasi */}
            <p className="text-xs text-slate-500 dark:text-slate-400 italic font-medium mb-3">
              {item.latin}
            </p>

            {/* Deskripsi (Preview) */}
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              {isExpanded
                ? item.description
                : `${(item.description || '').substring(0, 110)}${item.description?.length > 110 ? '...' : ''}`
              }
            </p>
          </div>

          {/* Tombol Aksi Kanan */}
          <div className="flex flex-col gap-2 flex-shrink-0">
            {/* Salin */}
            <button
              onClick={handleCopy}
              className="
                w-9 h-9 rounded-2xl
                bg-stone-100 dark:bg-slate-700
                hover:bg-stone-200 dark:hover:bg-slate-600
                flex items-center justify-center
                transition-all active:scale-90
              "
              title="Salin"
            >
              {copied
                ? <Check className="w-4 h-4 text-green-500" />
                : <Copy className="w-4 h-4 text-stone-500 dark:text-slate-400" />
              }
            </button>

            {/* Expand / Collapse */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="
                w-9 h-9 rounded-2xl
                bg-green-100 dark:bg-green-900/30
                hover:bg-green-200 dark:hover:bg-green-900/50
                flex items-center justify-center
                transition-all active:scale-90
              "
              title={isExpanded ? 'Tutup' : 'Lihat Detail'}
            >
              {isExpanded
                ? <ChevronUp className="w-4 h-4 text-green-700 dark:text-green-400" />
                : <ChevronDown className="w-4 h-4 text-green-700 dark:text-green-400" />
              }
            </button>
          </div>
        </div>
      </div>

      {/* ── Konten Detail (Collapsible) ──────────────── */}
      {isExpanded && (
        <div className="border-t border-stone-100 dark:border-slate-700 px-4 pb-4 pt-4 space-y-3 animate-fade-in">

          {/* Ciri-ciri */}
          {item.characteristics?.length > 0 && (
            <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/40 rounded-2xl p-3">
              <p className="text-[10px] font-extrabold text-sky-600 dark:text-sky-400 uppercase tracking-widest mb-2">
                Ciri-ciri
              </p>
              <ul className="space-y-1.5">
                {item.characteristics.map((char, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-sky-800 dark:text-sky-300">
                    <span className="text-sky-500 flex-shrink-0 mt-0.5">•</span>
                    <span className="leading-relaxed">{char}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Rumus / Pola */}
          {item.formula && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 rounded-2xl p-3">
              <p className="text-[10px] font-extrabold text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-2">
                Rumus / Pola
              </p>
              <p
                dir="rtl"
                className="text-xl font-bold text-amber-800 dark:text-amber-300 leading-relaxed"
                style={{ fontFamily: 'Amiri, serif' }}
              >
                {item.formula}
              </p>
            </div>
          )}

          {/* Contoh Penggunaan */}
          {item.example && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/40 rounded-2xl p-3 space-y-3">
              <p className="text-[10px] font-extrabold text-green-600 dark:text-green-400 uppercase tracking-widest">
                Contoh Penggunaan
              </p>

              {/* Teks Arab Contoh */}
              <div className="bg-white dark:bg-slate-800 border border-green-200 dark:border-green-800/40 rounded-2xl p-3">
                <p
                  dir="rtl"
                  className="text-2xl font-bold text-green-800 dark:text-green-300 leading-relaxed"
                  style={{ fontFamily: 'Amiri, serif' }}
                >
                  {item.example}
                </p>
              </div>

              {/* Analisis per Kata */}
              {item.exampleAnalysis?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-green-700 dark:text-green-400 uppercase tracking-wider">
                    Analisis:
                  </p>
                  {item.exampleAnalysis.map((analysis, i) => (
                    <div
                      key={i}
                      className="
                        bg-white dark:bg-slate-800
                        border border-green-200 dark:border-green-800/40
                        rounded-2xl p-2.5
                        flex items-center justify-between gap-3
                      "
                    >
                      <span
                        dir="rtl"
                        className="text-lg font-bold text-green-700 dark:text-green-300"
                        style={{ fontFamily: 'Amiri, serif' }}
                      >
                        {analysis.word}
                      </span>
                      <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 bg-green-100 dark:bg-green-900/30 px-2.5 py-1 rounded-xl text-right italic">
                        {analysis.role}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Terjemahan */}
              {item.translation && (
                <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800/40 rounded-2xl p-3">
                  <p className="text-[10px] font-extrabold text-green-700 dark:text-green-400 uppercase tracking-widest mb-1">
                    Terjemahan
                  </p>
                  <p className="text-xs font-medium text-green-900 dark:text-green-300 leading-relaxed italic">
                    "{item.translation}"
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Informasi Tambahan */}
          {item.additionalInfo && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/40 rounded-2xl p-3">
              <p className="text-[10px] font-extrabold text-purple-600 dark:text-purple-400 uppercase tracking-widest mb-2">
                Informasi Tambahan
              </p>

              {/* Tabel Huruf Jar */}
              {item.additionalInfo.hurufJar && (
                <div className="space-y-2">
                  <p className="text-[11px] font-bold text-purple-700 dark:text-purple-400">
                    Daftar Huruf Jar:
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {item.additionalInfo.hurufJar.map((h, i) => (
                      <div
                        key={i}
                        className="
                          bg-white dark:bg-slate-800
                          border border-purple-200 dark:border-purple-800/40
                          rounded-2xl p-2
                          flex items-center justify-between
                        "
                      >
                        <span
                          dir="rtl"
                          className="text-lg font-bold text-purple-700 dark:text-purple-300"
                          style={{ fontFamily: 'Amiri, serif' }}
                        >
                          {h.huruf}
                        </span>
                        <span className="text-[11px] text-slate-600 dark:text-slate-400 font-medium">
                          {h.arti}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Istilah Terkait */}
          {item.relatedTerms?.length > 0 && (
            <div className="bg-stone-50 dark:bg-slate-700/50 border border-stone-200 dark:border-slate-600 rounded-2xl p-3">
              <p className="text-[10px] font-extrabold text-stone-500 dark:text-slate-400 uppercase tracking-widest mb-2">
                Istilah Terkait
              </p>
              <div className="flex flex-wrap gap-1.5">
                {item.relatedTerms.map((termId, i) => (
                  <span
                    key={i}
                    className="
                      text-[11px] font-semibold
                      bg-white dark:bg-slate-700
                      text-slate-700 dark:text-slate-300
                      border border-stone-200 dark:border-slate-600
                      px-2.5 py-1 rounded-full
                    "
                  >
                    {termId.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}